import {useCurrentCompany, useCurrentCompanyId} from "@/api/companyApi";
import {
  GLOBAL_POOL_TAB,
  normalizeKeywordText,
  TProductLineDraft,
  useBulkAssignProductLinesMutation,
  useSelectedTiktokVisibilityCountry,
  useTiktokVisibilityCountriesQuery,
  useTiktokVisibilityNewProductLineRecommendationsQueries,
  useTiktokVisibilityProductLineAssignmentsQuery,
  useTiktokVisibilityProductLineRecommendationsQuery,
  useTiktokVisibilityProductLineScoresQuery,
} from "@/api/tiktokVisibilityApi";
import InfinityIcon from "@/assets/colorable/infinity.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import UndoIcon from "@/assets/colorable/undo.svg?react";
import WarningTriangleIcon from "@/assets/colorable/warning_triangle.svg?react";
import Button from "@/components/Common/Button";
import ConfirmationModal from "@/components/Common/ConfirmationModal";
import CollapsibleContent from "@/components/Common/forms/CollapsibleContent";
import Modal from "@/components/Common/Modal";
import ProductLineEditableSection from "@/components/TiktokSearch/ProductLineEditableSection";
import ProductLineModalTabs from "@/components/TiktokSearch/ProductLineModalTabs";
import ProductLineSidebar from "@/components/TiktokSearch/ProductLineSidebar";
import useCountries from "@/hooks/useCountries";
import {useIsAdmin} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {isManageProductLinesModalOpenAtom} from "@/store/jotaiAtoms";
import {usePaymentStore} from "@/store/payment";
import {isInfinite} from "@/utils/planUtils";
import {useSetAtom} from "jotai";
import difference from "lodash/difference";
import {useEffect, useLayoutEffect, useRef, useState, type FC} from "react";
import {Trans, useTranslation} from "react-i18next";
import * as RPNInput from "react-phone-number-input";
import {useEvent} from "react-use";
import {useImmer} from "use-immer";

type TItemField = "competitors" | "keywords";

const SUMMARY_KEYS = {
  adding: {both: "adding_summary_both", competitors: "adding_summary_competitors", keywords: "adding_summary_keywords"},
  removing: {
    both: "removing_summary_both",
    competitors: "removing_summary_competitors",
    keywords: "removing_summary_keywords",
  },
  tracking: {
    both: "tracking_summary_both",
    competitors: "tracking_summary_competitors",
    keywords: "tracking_summary_keywords",
  },
  untracking: {
    both: "untracking_summary_both",
    competitors: "untracking_summary_competitors",
    keywords: "untracking_summary_keywords",
  },
} as const;

const ManageProductLinesModal: FC = () => {
  const {t} = useTranslation(["search_visibility", "common"]);
  const setIsOpen = useSetAtom(isManageProductLinesModalOpenAtom);
  const companyProfileId = useCurrentCompanyId();
  const isAdmin = useIsAdmin();
  const {getCountryTranslatedLabelByCode} = useCountries();
  const {data: countriesData} = useTiktokVisibilityCountriesQuery({companyProfileId});
  const keywordLimit = countriesData?.keyword_limit ?? 0;
  const isKeywordLimitUnlimited = isInfinite(keywordLimit, "tiktok_visibility_keyword_limit");
  const selectedCountry = useSelectedTiktokVisibilityCountry({companyProfileId});
  const contactSales = usePaymentStore(state => state.contactSales);
  const {data: currentCompany} = useCurrentCompany();
  const [activeTab, setActiveTab] = useState<string>(GLOBAL_POOL_TAB);
  const [pendingTabSwitch, setPendingTabSwitch] = useState<string | null>(null);
  const [activeProductLine, setActiveProductLine] = useState<string>("");
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const [productLineDrafts, setProductLineDrafts] = useImmer<TProductLineDraft[]>([]);
  const defaultedCompetitorLinesRef = useRef<Set<string>>(new Set());
  const isGlobalTab = activeTab === GLOBAL_POOL_TAB;
  const activeCountry = isGlobalTab ? undefined : (activeTab as RPNInput.Country);

  const {data: productLineRecommendations, isLoading: isLoadingProductLineRecommendations} =
    useTiktokVisibilityProductLineRecommendationsQuery({companyProfileId});

  const {data: assignmentsData, isPending: isPoolLoading} = useTiktokVisibilityProductLineAssignmentsQuery({
    companyProfileId,
  });

  const poolLines = (assignmentsData?.product_lines ?? []).map(pl => ({
    competitors: pl.competitors.map(c => c.competitor_name),
    id: pl.id,
    isPrimary: pl.is_primary,
    keywords: pl.keywords.map(k => k.keyword_text),
    name: pl.name,
  }));

  const {data: activeCountryScoresData, isPending: isActiveCountryPending} = useTiktokVisibilityProductLineScoresQuery({
    companyProfileId,
    country: activeCountry,
    queryOptions: {enabled: !isGlobalTab && !!activeCountry},
  });

  const activeCountryScores = activeCountryScoresData?.product_lines ?? [];

  const isActiveTabLoading = isPoolLoading || (!isGlobalTab && isActiveCountryPending);

  const newProductLineDrafts = productLineDrafts.filter(line => !line.id && !line.isPendingDeletion);
  const newProductLineRecommendationsQueries = useTiktokVisibilityNewProductLineRecommendationsQueries({
    companyProfileId,
    names: newProductLineDrafts.map(line => line.name),
  });

  const isLoadingSuggestionsByName = Object.fromEntries(
    newProductLineDrafts.map((line, index) => [
      line.name.toLowerCase(),
      newProductLineRecommendationsQueries[index]?.isLoading ?? false,
    ]),
  );

  const recommendationsByName = Object.fromEntries(
    [
      ...(productLineRecommendations?.product_lines ?? []),
      ...newProductLineRecommendationsQueries.flatMap(query => query.data?.product_lines ?? []),
    ].map(pl => [
      pl.name.toLowerCase(),
      {
        suggestedCompetitors: pl.competitors.map(c => c.name),
        suggestedKeywords: pl.keywords.map(k => k.text),
      },
    ]),
  );

  const savedById = Object.fromEntries(
    poolLines.map(pl => [pl.id, {competitors: pl.competitors, keywords: pl.keywords}]),
  );

  // Stable content signatures keep the seed effect from re-running (and looping) on every render:
  // `poolLines`/`activeCountryScores` are freshly derived arrays with new identities each render, so
  // depending on them directly would reseed the drafts endlessly. The signatures only change when the
  // underlying server data actually changes.
  const poolSignature = poolLines
    .map(pl => `${pl.id}:${pl.name}:${pl.keywords.join(",")}:${pl.competitors.join(",")}`)
    .join("|");
  const activeCountrySignature = activeCountryScores
    .map(
      pl =>
        `${pl.name}:${pl.keywords.map(k => k.keyword_text).join(",")}:${pl.competitors.map(c => c.competitor_name).join(",")}`,
    )
    .join("|");
  const seedSignature = isGlobalTab ? poolSignature : `${poolSignature}|${activeCountrySignature}`;

  useLayoutEffect(() => {
    if (isActiveTabLoading) return;
    setProductLineDrafts(
      poolLines.map(pl => {
        const countryProductLine = isGlobalTab
          ? undefined
          : activeCountryScores.find(scored => scored.name === pl.name);

        return {
          competitors: isGlobalTab
            ? pl.competitors
            : (countryProductLine?.competitors.map(c => c.competitor_name) ?? []),
          id: pl.id,
          isPendingDeletion: false,
          isPrimary: pl.isPrimary,
          keywords: isGlobalTab ? pl.keywords : (countryProductLine?.keywords.map(k => k.keyword_text) ?? []),
          name: pl.name,
        };
      }),
    );
    setActiveProductLine(prev => prev || poolLines[0]?.name || "");
  }, [activeTab, seedSignature, isActiveTabLoading]);

  // For newly added product lines, toggle all recommended competitors on by default once their
  // recommendations load. Done once per line so the user can deselect without them snapping back.
  const newRecommendationsReadyKey = newProductLineDrafts
    .map((line, index) => `${line.name.toLowerCase()}:${newProductLineRecommendationsQueries[index]?.data ? "1" : "0"}`)
    .join(",");
  useEffect(() => {
    newProductLineDrafts.forEach((line, index) => {
      const key = line.name.toLowerCase();
      if (defaultedCompetitorLinesRef.current.has(key)) return;
      const data = newProductLineRecommendationsQueries[index]?.data;
      if (!data) return;
      defaultedCompetitorLinesRef.current.add(key);

      const suggestedCompetitors = recommendationsByName[key]?.suggestedCompetitors ?? [];
      if (suggestedCompetitors.length === 0) return;

      setProductLineDrafts(draft => {
        const target = draft.find(l => l.name === line.name && !l.id);
        if (!target) return;
        suggestedCompetitors.forEach(competitor => {
          if (!target.competitors.includes(competitor)) target.competitors.push(competitor);
        });
      });
    });
  }, [newRecommendationsReadyKey]);

  const marketBaselineByName = isGlobalTab
    ? undefined
    : Object.fromEntries(
        activeCountryScores.map(pl => [
          pl.name,
          {competitors: pl.competitors.map(c => c.competitor_name), keywords: pl.keywords.map(k => k.keyword_text)},
        ]),
      );

  const getProductLineDraftDiff = (productLine: TProductLineDraft) => {
    const baseline = isGlobalTab
      ? productLine.id
        ? savedById[productLine.id]
        : undefined
      : marketBaselineByName?.[productLine.name];
    const savedKeywords = baseline?.keywords ?? [];
    const savedCompetitors = baseline?.competitors ?? [];
    return {
      addedCompetitors: difference(productLine.competitors, savedCompetitors),
      addedKeywords: difference(productLine.keywords, savedKeywords),
      removedCompetitors: difference(savedCompetitors, productLine.competitors),
      removedKeywords: difference(savedKeywords, productLine.keywords),
      savedCompetitors,
      savedKeywords,
    };
  };

  const toggleItem = (productLineName: string, field: TItemField, value: string) => {
    setProductLineDrafts(draft => {
      const line = draft.find(l => l.name === productLineName);
      if (!line) return;

      const idx = line[field].indexOf(value);
      if (idx >= 0) line[field].splice(idx, 1);
      else line[field].push(value);
    });
  };

  const addCustomItem = (productLineName: string, field: TItemField, value: string) =>
    setProductLineDrafts(draft => {
      const line = draft.find(l => l.name === productLineName);
      if (!line) return;
      const lower = normalizeKeywordText(value).toLowerCase();
      if (line[field].some(item => normalizeKeywordText(item).toLowerCase() === lower)) return;
      const saved = line.id ? savedById[line.id] : undefined;
      const savedItem = saved?.[field].find(item => normalizeKeywordText(item).toLowerCase() === lower);
      line[field].push(savedItem ?? value);
    });

  const activeProductLineDrafts = productLineDrafts.filter(line => !line.isPendingDeletion);
  const productLineDraftDiffs = productLineDrafts.map(line => ({
    diff: getProductLineDraftDiff(line),
    line,
  }));
  const activeProductLineDraftDiffs = productLineDraftDiffs.filter(({line}) => !line.isPendingDeletion);

  const addedKeywords = activeProductLineDraftDiffs.flatMap(({diff}) => diff.addedKeywords);
  const addedCompetitors = activeProductLineDraftDiffs.flatMap(({diff}) => diff.addedCompetitors);
  const deletedProductLineDiffs = productLineDraftDiffs.filter(({line}) => line.isPendingDeletion);
  const removedKeywords = [
    ...activeProductLineDraftDiffs.flatMap(({diff}) => diff.removedKeywords),
    ...deletedProductLineDiffs.flatMap(({diff}) => diff.savedKeywords),
  ];
  const removedCompetitors = [
    ...activeProductLineDraftDiffs.flatMap(({diff}) => diff.removedCompetitors),
    ...deletedProductLineDiffs.flatMap(({diff}) => diff.savedCompetitors),
  ];
  const addedProductLines = newProductLineDrafts.map(line => line.name);
  const removedProductLines = deletedProductLineDiffs.map(({line}) => line.name);

  const savedPrimaryId = poolLines.find(pl => pl.isPrimary)?.id;
  const draftPrimary = activeProductLineDrafts.find(l => l.isPrimary);
  const isPrimaryChanged = isGlobalTab && draftPrimary?.id !== savedPrimaryId;

  const keywordUsageBase = countriesData?.keyword_usage ?? 0;
  const globalKeywordUsage = isGlobalTab
    ? keywordUsageBase + addedKeywords.length - removedKeywords.length
    : keywordUsageBase;
  const slotsRemaining = Math.max(0, keywordLimit - globalKeywordUsage);
  const isAtKeywordLimit =
    !!countriesData && !isKeywordLimitUnlimited && globalKeywordUsage >= keywordLimit && !isAdmin;
  const isApproachingKeywordLimit =
    !isKeywordLimitUnlimited && !isAtKeywordLimit && globalKeywordUsage / keywordLimit >= 0.8;
  const keywordProgressPct = Math.min(100, (globalKeywordUsage / keywordLimit) * 100);

  const trackedKeywordCountForCountry = productLineDrafts.reduce((sum, line) => sum + line.keywords.length, 0);

  const bulkAssignMutation = useBulkAssignProductLinesMutation({
    companyProfileId: companyProfileId ?? null,
    country: activeCountry,
  });

  const buildCountSummary = ({
    compCount,
    kwCount,
    verb,
  }: {
    compCount: number;
    kwCount: number;
    verb: keyof typeof SUMMARY_KEYS;
  }) => {
    if (kwCount === 0 && compCount === 0) return null;
    const keys = SUMMARY_KEYS[verb];
    const interpolation = {
      competitors: t("competitors_count", {count: compCount}),
      keywords: t("keywords_count", {count: kwCount}),
    };
    if (kwCount > 0 && compCount > 0) return t(keys.both, interpolation);
    if (kwCount > 0) return t(keys.keywords, interpolation);
    return t(keys.competitors, interpolation);
  };

  const summaryAddVerb = isGlobalTab ? "adding" : "tracking";
  const summaryRemoveVerb = isGlobalTab ? "removing" : "untracking";
  const changeSummaries = productLineDraftDiffs.flatMap(({diff, line}) => {
    const isNew = !line.id;
    const isDeleted = line.isPendingDeletion;
    const hasItemChanges =
      !isDeleted &&
      diff.addedKeywords.length +
        diff.addedCompetitors.length +
        diff.removedKeywords.length +
        diff.removedCompetitors.length >
        0;
    if (!isNew && !isDeleted && !hasItemChanges) return [];

    return [
      {
        additionsSentence: isDeleted
          ? null
          : buildCountSummary({
              compCount: diff.addedCompetitors.length,
              kwCount: diff.addedKeywords.length,
              verb: summaryAddVerb,
            }),
        isDeleted,
        isNew,
        name: line.name,
        removalsSentence: buildCountSummary({
          compCount: isDeleted ? diff.savedCompetitors.length : diff.removedCompetitors.length,
          kwCount: isDeleted ? diff.savedKeywords.length : diff.removedKeywords.length,
          verb: summaryRemoveVerb,
        }),
      },
    ];
  });

  const hasPending = changeSummaries.length > 0 || isPrimaryChanged;

  const sidebarItems = productLineDraftDiffs.map(({diff, line}) => ({
    additionCount: diff.addedKeywords.length + diff.addedCompetitors.length,
    isPendingDeletion: line.isPendingDeletion,
    isPrimary: line.isPrimary,
    name: line.name,
    removalCount: diff.removedKeywords.length + diff.removedCompetitors.length,
  }));

  useEvent(
    "scroll",
    () => {
      if (!scrollEl) return;
      const ACTIVE_SECTION_TOP_OFFSET = 120;
      const containerTop = scrollEl.getBoundingClientRect().top;
      const isBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 4;

      if (isBottom) {
        const last = productLineDrafts[productLineDrafts.length - 1]?.name;
        if (last) setActiveProductLine(last);
        return;
      }

      const active = productLineDrafts.find(line => {
        const el = sectionRefs.current[line.name];
        if (!el) return false;
        const top = el.getBoundingClientRect().top - containerTop;
        return top >= 0 && top <= ACTIVE_SECTION_TOP_OFFSET;
      });
      if (active) setActiveProductLine(active.name);
    },
    scrollEl,
  );

  const saveActiveTab = (onDone: () => void) => {
    if (!companyProfileId || !hasPending) return;

    const body = {
      product_lines: activeProductLineDrafts.map(line => ({
        competitors: line.competitors,
        is_primary: line.isPrimary,
        keywords: line.keywords,
        name: line.name,
      })),
    };
    if (body.product_lines.length === 0) return;

    bulkAssignMutation.mutate(body, {
      onSuccess: () => {
        if (isGlobalTab) {
          capturePostHogEvent(PostHogEvents.TiktokVisibilityProductLinesUpdated, {
            addedCompetitors,
            addedCompetitorsCount: addedCompetitors.length,
            addedKeywords,
            addedKeywordsCount: addedKeywords.length,
            addedProductLines,
            addedProductLinesCount: addedProductLines.length,
            removedCompetitors,
            removedCompetitorsCount: removedCompetitors.length,
            removedKeywords,
            removedKeywordsCount: removedKeywords.length,
            removedProductLines,
            removedProductLinesCount: removedProductLines.length,
          });
        }
        onDone();
      },
    });
  };

  const saveLabel = isGlobalTab
    ? t("save_pool")
    : t("save_market", {country: getCountryTranslatedLabelByCode(activeCountry!)});

  const performTabSwitch = (target: string) => {
    setPendingTabSwitch(null);
    setActiveTab(target);
  };

  return (
    <>
      <Modal
        onClose={() => setIsOpen(false)}
        backgroundClassName="w-[95vw] md:w-4/6 max-w-[920px] h-[90vh] max-h-[95vh] md:max-h-[90vh]"
        className="flex min-h-0 grow flex-col gap-0 overflow-hidden p-0"
        customTitleRow={
          <div className="flex w-full flex-col">
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-col gap-1 p-4 md:p-5">
                <span className="text-neutral-black text-base font-semibold md:text-xl">
                  {t("manage_product_lines")}
                </span>
                <span className="text-neutral-grey-dark text-xs">
                  {isGlobalTab
                    ? t("manage_product_lines_pool_subtitle")
                    : t("manage_product_lines_market_subtitle", {
                        country: getCountryTranslatedLabelByCode(activeCountry!),
                      })}
                </span>
              </div>
              <button onClick={() => setIsOpen(false)} className="mr-5 h-fit" disabled={bulkAssignMutation.isPending}>
                <TimesIcon className="text-neutral-grey-light hover:text-neutral-grey-dark size-5 transition-colors duration-150" />
              </button>
            </div>
            <div className="bg-neutral-white-light border-neutral-white-dark flex w-full flex-col gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 md:px-5">
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-neutral-grey-dark inline-flex flex-wrap items-center gap-1 text-xs">
                  <Trans
                    t={t}
                    i18nKey="search_visibility:keywords_used"
                    count={globalKeywordUsage}
                    values={{count: globalKeywordUsage, limit: isKeywordLimitUnlimited ? "" : keywordLimit}}
                    components={{
                      1: (
                        <span
                          className={clsxm(
                            "inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-semibold",
                            isAtKeywordLimit
                              ? "border-warning-medium-yellow bg-warning-light-yellow text-warning-dark-yellow"
                              : isApproachingKeywordLimit
                                ? "bg-warning-light-yellow text-warning-dark-yellow border-transparent"
                                : "border-neutral-white-dark text-neutral-black bg-white",
                          )}
                        />
                      ),
                      2: isKeywordLimitUnlimited ? (
                        <InfinityIcon className="inline-block h-3 w-4 shrink-0 align-middle" />
                      ) : (
                        <></>
                      ),
                    }}
                  />
                </span>
                {!isGlobalTab ? (
                  <span className="text-bold-blue-700 text-xs font-medium">
                    {t("tracked_in_country", {
                      count: trackedKeywordCountForCountry,
                      country: getCountryTranslatedLabelByCode(activeCountry!),
                    })}
                  </span>
                ) : (
                  !isKeywordLimitUnlimited && (
                    <span
                      className={clsxm(
                        "text-xs",
                        isAtKeywordLimit ? "text-warning-dark-yellow font-medium" : "text-neutral-grey-dark",
                      )}>
                      {isAtKeywordLimit ? (
                        <Trans
                          t={t}
                          i18nKey="search_visibility:limit_reached"
                          components={{
                            1: (
                              <button
                                type="button"
                                className="text-primary-dark-solid cursor-pointer font-semibold underline underline-offset-2"
                                onClick={() =>
                                  contactSales(t("keyword_limit_reached_subject"), currentCompany?.company_name ?? "")
                                }
                              />
                            ),
                          }}
                        />
                      ) : (
                        t("slots_remaining_keywords", {count: slotsRemaining})
                      )}
                    </span>
                  )
                )}
              </div>
              {!isKeywordLimitUnlimited && (
                <div className="bg-soft-grey-500 h-2 w-full shrink-0 overflow-hidden rounded-full sm:w-24">
                  <div
                    className={clsxm(
                      "h-full rounded-full transition-all",
                      isAtKeywordLimit || isApproachingKeywordLimit
                        ? "bg-warning-medium-yellow"
                        : "bg-primary-dark-solid",
                    )}
                    style={{width: `${keywordProgressPct}%`}}
                  />
                </div>
              )}
            </div>
            {selectedCountry && (
              <ProductLineModalTabs
                activeTab={activeTab}
                pendingCount={changeSummaries.length}
                tabs={[
                  {id: GLOBAL_POOL_TAB, label: t("global_pool")},
                  {
                    flagCountry: selectedCountry,
                    id: selectedCountry,
                    label: getCountryTranslatedLabelByCode(selectedCountry),
                  },
                ]}
                onSelect={target => {
                  if (target === activeTab) return;
                  if (hasPending) {
                    setPendingTabSwitch(target);
                    return;
                  }
                  performTabSwitch(target);
                }}
              />
            )}
            <div
              className={clsxm(
                "flex items-start gap-2 px-4 py-2.5 text-xs md:px-5",
                isGlobalTab
                  ? "bg-primary-light-solid/50 text-primary-dark-solid"
                  : "bg-bold-blue-100/40 text-bold-blue-700", // TODO: update the color from index.css
              )}>
              <span className="leading-relaxed">
                {isGlobalTab
                  ? t("pool_hint")
                  : t("market_hint", {country: getCountryTranslatedLabelByCode(activeCountry!)})}
              </span>
            </div>
          </div>
        }
        customBottomRow={
          <div className="border-neutral-white-dark flex shrink-0 flex-wrap items-start justify-between gap-3 border-t p-4 md:p-5">
            {hasPending ? (
              <CollapsibleContent
                className="grow gap-1"
                lineHeight={24}
                shownLinesLimit={5}
                viewMoreClassName="text-primary-dark-solid text-xs">
                <div className="flex flex-col gap-2 text-xs">
                  {changeSummaries.map(summary => (
                    <div key={summary.name} className="flex flex-col gap-0.5">
                      <span className="text-neutral-black flex items-center gap-1.5 text-xs font-semibold">
                        {summary.name}
                        {summary.isNew && (
                          <span className="bg-primary-light-solid text-primary-dark-solid text-2xs rounded-full px-1.5 py-0.5 font-medium">
                            {t("common:new")}
                          </span>
                        )}
                        {summary.isDeleted && (
                          <span className="bg-danger-light-red text-danger-medium-red text-2xs rounded-full px-1.5 py-0.5 font-medium capitalize">
                            {t("deleted")}
                          </span>
                        )}
                      </span>
                      {summary.additionsSentence && (
                        <span className="text-neutral-grey-dark pl-2">{summary.additionsSentence}</span>
                      )}
                      {summary.removalsSentence && (
                        <span className="text-neutral-grey-dark pl-2">{summary.removalsSentence}</span>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            ) : (
              <span className="text-neutral-grey-dark text-xs">
                {isGlobalTab ? t("pick_suggestions_to_update") : t("untrack_hint")}
              </span>
            )}
            <div className="ml-auto flex gap-3">
              <Button buttonStyle="White" className="h-10 px-4 md:px-6" onClick={() => setIsOpen(false)}>
                {t("common:cancel")}
              </Button>
              <Button
                buttonStyle="Gradient"
                className="h-10 px-4 md:px-6"
                isDisabled={!hasPending || !companyProfileId || isActiveTabLoading}
                isLoading={bulkAssignMutation.isPending}
                onClick={() => saveActiveTab(() => setIsOpen(false))}>
                {saveLabel}
              </Button>
            </div>
          </div>
        }
        closeButtonClassName="hidden"
        isLoading={bulkAssignMutation.isPending}>
        <div className="flex min-h-0 grow flex-row overflow-hidden">
          <ProductLineSidebar
            activeProductLine={activeProductLine}
            isReadOnly={!isGlobalTab}
            items={sidebarItems}
            scrollToProductLine={name =>
              sectionRefs.current[name]?.scrollIntoView({behavior: "smooth", block: "start"})
            }
            setProductLineDrafts={setProductLineDrafts}
          />
          <div ref={setScrollEl} className="flex-1 overflow-y-auto px-4 py-4 md:px-7 md:py-5">
            {productLineDraftDiffs.map(({diff, line}, productLineIdx) => {
              const productLineAdditionCount = diff.addedKeywords.length + diff.addedCompetitors.length;
              const productLineRemovalCount = diff.removedKeywords.length + diff.removedCompetitors.length;
              const recommendations = recommendationsByName[line.name.toLowerCase()];
              const isLoadingSuggestions =
                isLoadingProductLineRecommendations || (isLoadingSuggestionsByName[line.name.toLowerCase()] ?? false);
              const poolLine = poolLines.find(pl => pl.name === line.name);
              const trackedKeywords = new Set(line.keywords.map(keyword => keyword.toLowerCase()));
              const trackedCompetitors = new Set(line.competitors.map(competitor => competitor.toLowerCase()));

              const pendingCount = productLineAdditionCount + productLineRemovalCount;

              return (
                <div
                  key={line.name}
                  ref={el => {
                    sectionRefs.current[line.name] = el;
                  }}
                  className={clsxm(
                    "flex scroll-mt-2 flex-col gap-5",
                    productLineIdx > 0 && "border-neutral-white-dark mt-8 border-t pt-8",
                  )}>
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={clsxm(
                        "text-base font-bold",
                        line.isPendingDeletion ? "text-neutral-grey-dark line-through" : "text-neutral-black",
                      )}>
                      {line.name}
                    </span>
                    {!line.isPendingDeletion && pendingCount > 0 && (
                      <span className="text-primary-dark-solid shrink-0 text-xs font-medium">
                        {pendingCount} {t("common:pending")}
                      </span>
                    )}
                  </div>
                  {line.isPendingDeletion ? (
                    <div className="bg-danger-light-red border-danger-medium-red/30 flex items-center justify-between gap-3 rounded-lg border px-4 py-3">
                      <span className="text-danger-dark-red text-sm">{t("product_line_marked_for_deletion")}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setProductLineDrafts(draft => {
                            const target = draft.find(l => l.name === line.name);
                            if (target) target.isPendingDeletion = false;
                          })
                        }
                        className="text-primary-dark-solid hover:text-primary-dark-solid/80 flex shrink-0 cursor-pointer items-center gap-1 text-sm font-medium transition-colors">
                        <UndoIcon className="size-3 shrink-0" />
                        {t("common:undo")}
                      </button>
                    </div>
                  ) : isGlobalTab ? (
                    <>
                      <ProductLineEditableSection
                        addButtonLabel={t("add_keyword")}
                        title={t("keywords")}
                        inputPlaceholder={t("add_your_own_keyword")}
                        currentItems={diff.savedKeywords}
                        isAddingDisabled={isAtKeywordLimit}
                        isLoadingSuggestions={isLoadingSuggestions}
                        lockable
                        lockedHintText={t("locked_keyword_pool_hint")}
                        pendingItems={diff.addedKeywords}
                        removedCurrentItems={diff.removedKeywords}
                        suggestedItems={recommendations?.suggestedKeywords ?? []}
                        onToggle={value => toggleItem(line.name, "keywords", value)}
                        onAddCustom={value => addCustomItem(line.name, "keywords", value)}
                      />
                      <ProductLineEditableSection
                        addButtonLabel={t("add_competitor")}
                        title={t("competitors")}
                        inputPlaceholder={t("add_your_own_competitor")}
                        currentItems={diff.savedCompetitors}
                        isLoadingSuggestions={isLoadingSuggestions}
                        pendingItems={diff.addedCompetitors}
                        removedCurrentItems={diff.removedCompetitors}
                        suggestedItems={recommendations?.suggestedCompetitors ?? []}
                        onToggle={value => toggleItem(line.name, "competitors", value)}
                        onAddCustom={value => addCustomItem(line.name, "competitors", value)}
                      />
                    </>
                  ) : (
                    <>
                      <ProductLineEditableSection
                        title={t("keywords")}
                        showInput={false}
                        lockable
                        lockedHintText={t("locked_keyword_market_hint")}
                        currentItems={poolLine?.keywords ?? []}
                        untrackedItems={(poolLine?.keywords ?? []).filter(
                          keyword => !trackedKeywords.has(keyword.toLowerCase()),
                        )}
                        onToggle={value => toggleItem(line.name, "keywords", value)}
                      />
                      <ProductLineEditableSection
                        title={t("competitors")}
                        showInput={false}
                        currentItems={poolLine?.competitors ?? []}
                        untrackedItems={(poolLine?.competitors ?? []).filter(
                          competitor => !trackedCompetitors.has(competitor.toLowerCase()),
                        )}
                        onToggle={value => toggleItem(line.name, "competitors", value)}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
      {pendingTabSwitch && (
        <ConfirmationModal
          tone="warning"
          Icon={WarningTriangleIcon}
          title={t("unsaved_changes_title")}
          description={t("unsaved_changes_description")}
          descriptionClassName="text-center"
          isPending={bulkAssignMutation.isPending}
          cancelButtonText={t("discard_changes")}
          onCancelButtonClick={() => performTabSwitch(pendingTabSwitch)}
          confirmButtonText={saveLabel}
          onConfirm={() => saveActiveTab(() => performTabSwitch(pendingTabSwitch))}
          onClose={() => setPendingTabSwitch(null)}
        />
      )}
    </>
  );
};

export default ManageProductLinesModal;
